/**
 * ScoringMonitor — iter87.199.81
 *
 * Split view. Left: `memory/scoring_registry.md` rendered as
 * structured rows (event · payout · backend source · preview row).
 * Right: crawled catalog of user-facing toast/modal scoring copy
 * grouped by file. Both read-only — copy changes ship via the
 * canon-locked registry.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Trophy, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TYPE_TONE = {
    success: "text-neon border-neon/40 bg-neon/5",
    error: "text-heat border-heat/40 bg-heat/5",
    info: "text-volt border-volt/40 bg-volt/5",
    warning: "text-cyber border-cyber/40 bg-cyber/5",
};

export default function ScoringMonitor() {
    const [reg, setReg] = useState({ sections: [], rows: [] });
    const [copyRows, setCopyRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [regQ, setRegQ] = useState("");
    const [copyQ, setCopyQ] = useState("");
    const [section, setSection] = useState("all");

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.siteCommsMonitorScoringRegistry(),
            api.siteCommsMonitorScoringCopy(),
        ]).then(([r, c]) => {
            setReg(r);
            setCopyRows(c.strings || []);
        }).catch((e) => toast.error(e?.response?.data?.detail || "Could not load scoring monitor"))
          .finally(() => setLoading(false));
    }, []);

    const filteredRows = useMemo(() => {
        const n = regQ.trim().toLowerCase();
        return (reg.rows || []).filter((r) => {
            if (section !== "all" && r.section !== section) return false;
            if (!n) return true;
            return (
                (r.event || "").toLowerCase().includes(n)
                || (r.backend_source || "").toLowerCase().includes(n)
                || (r.default_payout || "").toLowerCase().includes(n)
            );
        });
    }, [reg.rows, regQ, section]);

    const filteredCopy = useMemo(() => {
        const n = copyQ.trim().toLowerCase();
        if (!n) return copyRows;
        return copyRows.filter((c) =>
            (c.copy || "").toLowerCase().includes(n)
            || (c.file || "").toLowerCase().includes(n)
        );
    }, [copyRows, copyQ]);

    return (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="scoring-monitor">
            {/* ─── LEFT: registry viewer ─── */}
            <div className="brutal-card p-4 space-y-3">
                <div className="flex items-baseline gap-2">
                    <Trophy className="w-4 h-4 text-volt" />
                    <h2 className="font-display font-black text-lg">Scoring Registry</h2>
                    <span className="text-xs text-zinc-500">
                        {loading ? "loading…" : `${reg.rows?.length || 0} rows`}
                    </span>
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed">
                    Canon: <code className="text-zinc-300">memory/scoring_registry.md</code>. Grep-locked to backend
                    constants + <code className="text-zinc-300">Start.jsx::scoringPreview()</code> by pytest —
                    changing a scoring default in code without updating this file fails CI.
                </p>
                <input
                    type="text"
                    placeholder="Filter by event, source, or payout…"
                    value={regQ}
                    onChange={(e) => setRegQ(e.target.value)}
                    className="brutal-input w-full text-sm"
                    data-testid="scoring-registry-search"
                />
                <div className="flex flex-wrap gap-1.5">
                    <button
                        onClick={() => setSection("all")}
                        data-testid="scoring-registry-section-all"
                        className={`pill text-[9px] font-black uppercase tracking-widest px-2 py-0.5 ${
                            section === "all" ? "bg-volt text-black border-volt" : "bg-transparent text-zinc-400 border-[#333]"
                        }`}
                    >
                        all
                    </button>
                    {(reg.sections || []).map((s) => (
                        <button
                            key={s}
                            onClick={() => setSection(s)}
                            className={`pill text-[9px] font-black uppercase tracking-widest px-2 py-0.5 max-w-[220px] truncate ${
                                section === s ? "bg-volt text-black border-volt" : "bg-transparent text-zinc-400 border-[#333]"
                            }`}
                            title={s}
                        >
                            {s.split(" · ")[0]}
                        </button>
                    ))}
                </div>
                <div className="overflow-x-auto" data-testid="scoring-registry-table">
                    <table className="w-full text-sm">
                        <thead className="border-b-2 border-zinc-800">
                            <tr>
                                {["Event", "Payout", "Source", "Preview row"].map((h) => (
                                    <th key={h} className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-black">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRows.map((r, i) => (
                                <tr key={`${r.section}-${r.event}-${i}`} className="border-b border-zinc-900">
                                    <td className="px-2 py-1.5 text-white text-xs">{r.event}</td>
                                    <td className="px-2 py-1.5 font-mono text-[11px] text-volt">{r.default_payout}</td>
                                    <td className="px-2 py-1.5 font-mono text-[10px] text-zinc-400 max-w-[200px] truncate" title={r.backend_source}>{r.backend_source}</td>
                                    <td className="px-2 py-1.5 font-mono text-[10px] text-zinc-500 max-w-[220px] truncate" title={r.preview_row}>{r.preview_row}</td>
                                </tr>
                            ))}
                            {!loading && filteredRows.length === 0 && (
                                <tr><td colSpan={4} className="px-2 py-4 text-center text-xs text-zinc-500">No matches</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* ─── RIGHT: copy catalog ─── */}
            <div className="brutal-card p-4 space-y-3">
                <div className="flex items-baseline gap-2">
                    <MessageSquare className="w-4 h-4 text-volt" />
                    <h2 className="font-display font-black text-lg">Toast & Modal Copy</h2>
                    <span className="text-xs text-zinc-500">
                        {loading ? "loading…" : `${copyRows.length} strings`}
                    </span>
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed">
                    Static crawl of every <code className="text-zinc-300">toast.*</code> call across
                    <code className="text-zinc-300"> components/admin</code> and
                    <code className="text-zinc-300"> components/dashboard</code>. Copy changes ship
                    via the file edit — no in-app editor.
                </p>
                <input
                    type="text"
                    placeholder="Filter by copy or file…"
                    value={copyQ}
                    onChange={(e) => setCopyQ(e.target.value)}
                    className="brutal-input w-full text-sm"
                    data-testid="scoring-copy-search"
                />
                <div className="overflow-y-auto max-h-[520px] pr-1" data-testid="scoring-copy-list">
                    {filteredCopy.map((c, i) => (
                        <div key={`${c.file}:${c.line}-${i}`} className="border-b border-zinc-900 py-2 flex items-start gap-2">
                            <span className={`pill text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 shrink-0 ${TYPE_TONE[c.type] || TYPE_TONE.info}`}>
                                {c.type}
                            </span>
                            <div className="flex-1 min-w-0">
                                <p className="text-[11px] text-white leading-snug">{c.copy}</p>
                                <p className="text-[9px] text-zinc-600 font-mono mt-0.5 truncate">{c.file}:{c.line}</p>
                            </div>
                        </div>
                    ))}
                    {!loading && filteredCopy.length === 0 && (
                        <p className="text-xs text-zinc-500 py-4 text-center">No matches</p>
                    )}
                </div>
            </div>
        </div>
    );
}
